import { BehaviorSubject, Subject } from 'rxjs';
import * as uuid from 'uuid';

import { Platform } from '@angular/cdk/platform';

import { SldpSettings, DEFAULT_SLDP_SETTINGS, SnCamera } from '@lib/api/entities';
import { FsDataDogRumService } from '@lib/data-dog-rum';
import { SnPlayerState } from '@lib/player/common';

import { StreamAnalysisService } from '../services/stream-analysis-service';
import { SnVideoPlayerType } from '../video-player.enums';
import { SnVideoPlayerReference } from '../video-player.reference';
import { PLAYERS_REFERENCE } from './players.reference.constants';
import { emitStreamError } from './stream-error.utils';

// HTML5 player SDK documentation:: https://softvelum.com/player/web/sdk_documentation/#on_latency_adjust_seek //

declare const SLDP: any;

interface SldpStreamInfo {
  name?: string;
  width?: number;
  height?: number;
  vcodec?: string;
  acodec?: string;
  video?: string;
  audio?: string;
  bandwidth?: number | string;
}

export class SnSldpPlayerReference extends SnVideoPlayerReference {
  public static nextId = 0;
  private _callback: Function;
  private _error: Function;
  private _player: any;
  private _isPlaying: boolean = false;
  private readonly _playerContentElement: HTMLElement;
  private _playerWrapper: HTMLDivElement;
  private _firstPlay: boolean = true;
  private _initTime: number;
  private _isActive: boolean = false;
  private _camId: string;
  private _streamUrl: string;
  private _sldpSettings: SldpSettings;
  private _camera?: SnCamera;
  private _lowBufferCount: number = 0;
  private _stallStartTime: number = null;

  get isPlaying(): boolean {
    return this._isPlaying;
  }

  get isActive(): boolean {
    return this._isActive;
  }

  set isActive(value: boolean) {
    this._isActive = value;
  }

  constructor(
    playerElement: HTMLElement,
    playerContentElement: HTMLElement,
    src: string,
    autoplay: boolean,
    platform: Platform,
    state$: BehaviorSubject<SnPlayerState>,
    public currentTime$: BehaviorSubject<number>,
    bufferTime$: BehaviorSubject<number>,
    totalTime$: BehaviorSubject<number>,
    volume$: BehaviorSubject<number>,
    played$: BehaviorSubject<any>,
    loaded$: Subject<any>,
    ended$: Subject<void>,
    callback: Function,
    error: Function,
    private readonly _datadogService: FsDataDogRumService,
    camId: string,
    sldpSettings?: Partial<SldpSettings>,
    private readonly _streamAnalysisService?: StreamAnalysisService,
    camera?: SnCamera,
  ) {
    super(
      SnVideoPlayerType.SLDP,
      playerElement,
      platform,
      state$,
      currentTime$,
      bufferTime$,
      totalTime$,
      volume$,
      played$,
      loaded$,
      ended$,
    );
    this._initTime = new Date().getTime();
    this._callback = callback;
    this._error = error;
    this._playerContentElement = playerContentElement;
    this._playerContentElement.id = `sn-sldp-player-${uuid.v4()}`;
    this._camId = camId;
    this._camera = camera;
    this._init(this._playerContentElement, src, autoplay, sldpSettings, camera);
  }

  public play(): void {
    this._isPlaying = true;
    this._player.play();
  }

  public pause(): void {
    this._isPlaying = true;
    this._player.pause();
  }

  public destroy(): void {
    if (this.isActive && this._firstPlay) {
      this._firstPlay = false;
      this._streamAttemptEndAction('canceled');
      const streamContext = this._streamAnalysisService?.getStreamContextStart(this._camera);
      this._datadogService.addAction('livestream_attempt_end', {
        camera_id: this._camId,
        duration_ms: new Date().getTime() - this._initTime,
        status: 'canceled',
        buffering: this._sldpSettings.buffering,
        latency_tolerance: this._sldpSettings.latencyTolerance,
        latency_adjust_method: this._sldpSettings.latencyAdjustMethod,
        ...streamContext,
      });
    } else if (!this._firstPlay && this.isActive) {
      const streamContext = this._streamUrl.includes('cloud')
        ? this._streamAnalysisService?.getStreamContextEnd(this._camera, true)
        : this._streamAnalysisService?.getStreamContextEnd(this._camera, false);
      this._datadogService.addAction('active_livestream_end', {
        camera_id: this._camId,
        duration_ms: new Date().getTime() - this._initTime,
        status: 'closed',
        ...streamContext,
      });
    }
    this._error = null;
    super.destroy();
    this._player.destroy();
    this.videoElement.remove();
    this._playerWrapper?.remove();
  }

  private _init(
    playerContentElement: HTMLElement,
    src: string,
    autoplay: boolean,
    sldpSettings?: Partial<SldpSettings>,
    camera?: SnCamera,
  ): void {
    this._camera = camera;
    this._streamUrl = src;
    this._playerWrapper = document.createElement('div');
    this._playerWrapper.className = 'sldp_player_wrp sldp_player_wrp_video';
    const video = document.createElement('video');
    video.id = uuid.v4();
    this._playerWrapper.append(video);
    playerContentElement.append(this._playerWrapper);

    this._sldpSettings = {
      latencyTolerance: sldpSettings?.latencyTolerance ?? DEFAULT_SLDP_SETTINGS.latencyTolerance,
      latencyAdjustMethod:
        sldpSettings?.latencyAdjustMethod ?? DEFAULT_SLDP_SETTINGS.latencyAdjustMethod,
      buffering: sldpSettings?.buffering ?? DEFAULT_SLDP_SETTINGS.buffering,
    };

    this._player = SLDP.init({
      video_element: video.id,
      container: playerContentElement.id,
      stream_url: src,
      controls: false,
      autoplay: autoplay || this._isPlaying,
      latency_tolerance: this._sldpSettings.latencyTolerance,
      latency_adjust_method: this._sldpSettings.latencyAdjustMethod,
      buffering: this._sldpSettings.buffering,
      muted: true,
    });

    this._player.setCallbacks({
      onError: this._onError.bind(this),
      onConnectionStarted: this._onConnectionStarted.bind(this),
      onConnectionEstablished: this._onConnectionEstablished.bind(this),
      onConnectionClosed: this._onConnectionClosed.bind(this),
      onLatencyAdjustSeek: this._onLatencyAdjustSeek.bind(this),
      onLowBuffer: this._onLowBuffer.bind(this),
    });

    const streamContext = this._streamAnalysisService?.getStreamContextStart(this._camera);
    this._datadogService.addAction('stream_attempt_start', {
      ...streamContext,
      camera_id: this._camId,
      source_ui: PLAYERS_REFERENCE.SOURCE_UI.SLDP,
      protocol: PLAYERS_REFERENCE.PROTOCOL.SLDP,
    });

    const videoElements = playerContentElement.getElementsByTagName('video');
    const sldpVideoElement = videoElements.item(videoElements.length - 1);
    if (sldpVideoElement) {
      this.videoElement = sldpVideoElement;
      this.videoElement.onerror = (event: any) => {
        console.error('Video Element Error: ', event.target.error);
      };
      this.videoElement.addEventListener('playing', () => {
        if (this._stallStartTime) {
          const stallEndTime = performance.now();
          this._datadogService.addAction('stream_stall_duration', {
            timestamp: new Date().getTime(),
            camera_id: this._camId,
            source_ui: PLAYERS_REFERENCE.SOURCE_UI.SLDP,
            protocol: PLAYERS_REFERENCE.PROTOCOL.SLDP,
            stall_duration_ms: Math.round(stallEndTime - this._stallStartTime),
          });
          this._stallStartTime = null;
        }
        if (this._firstPlay && this._isActive) {
          this._firstPlay = false;
          this._streamAttemptEndAction('success');
          const streamContext = this._streamAnalysisService?.getStreamContextStart(this._camera);
          this._datadogService.addAction('livestream_attempt_end', {
            camera_id: this._camId,
            duration_ms: new Date().getTime() - this._initTime,
            status: 'success',
            buffering: this._sldpSettings.buffering,
            latency_tolerance: this._sldpSettings.latencyTolerance,
            latency_adjust_method: this._sldpSettings.latencyAdjustMethod,
            ...streamContext,
          });
        }
      });
    }
  }

  private _onError(error: string): void {
    if (this._firstPlay && this._isActive) {
      this._firstPlay = false;
      emitStreamError({
        datadogService: this._datadogService,
        cameraId: this._camId,
        protocol: PLAYERS_REFERENCE.PROTOCOL.SLDP,
        sourceUi: PLAYERS_REFERENCE.SOURCE_UI.SLDP,
        errorType: 'Stream error',
        rawError: error,
        errorCode: error || 'UNKNOWN',
        isLive: this.live,
        isFatal: true,
        protocolSpecificFields: {
          error_details: error,
        },
      });
      this._streamAttemptEndAction('fail');
      const streamContext = this._streamAnalysisService?.getStreamContextStart(this._camera);
      this._datadogService.addAction('livestream_attempt_end', {
        camera_id: this._camId,
        duration_ms: new Date().getTime() - this._initTime,
        status: 'fail',
        raw_error: error,
        buffering: this._sldpSettings.buffering,
        latency_tolerance: this._sldpSettings.latencyTolerance,
        latency_adjust_method: this._sldpSettings.latencyAdjustMethod,
        ...streamContext,
      });
    } else if (!this._firstPlay && this._isActive) {
      emitStreamError({
        datadogService: this._datadogService,
        cameraId: this._camId,
        protocol: PLAYERS_REFERENCE.PROTOCOL.SLDP,
        sourceUi: PLAYERS_REFERENCE.SOURCE_UI.SLDP,
        errorType: 'Stream error',
        rawError: error,
        errorCode: error || 'UNKNOWN',
        isLive: this.live,
        isFatal: true,
        protocolSpecificFields: {
          error_details: error,
        },
      });
      const streamContext = this._streamUrl.includes('cloud')
        ? this._streamAnalysisService?.getStreamContextEnd(this._camera, true)
        : this._streamAnalysisService?.getStreamContextEnd(this._camera, false);
      this._datadogService.addAction('active_livestream_end', {
        camera_id: this._camId,
        duration_ms: new Date().getTime() - this._initTime,
        status: 'error',
        raw_error: error,
        buffering: this._sldpSettings.buffering,
        latency_tolerance: this._sldpSettings.latencyTolerance,
        latency_adjust_method: this._sldpSettings.latencyAdjustMethod,
        ...streamContext,
      });
    }
    switch (error) {
      case 'NO_PLAYABLE_SOURCE_FOUND':
        setTimeout(() => {
          if (this._error) {
            this._error();
          }
          const wrapper = this._playerContentElement.querySelector('.sldp_player_wrp');
          const messagewrap = document.createElement('div');
          messagewrap.className = 'sldp_message_wrp sldp_message_wrp_video';
          const message = document.createElement('div');
          message.className = 'sldp_message sldp_message_pad';
          messagewrap.append(message);
          if (wrapper) {
            wrapper.append(messagewrap);
          }
          message.insertAdjacentHTML(
            'afterbegin',
            this._displayError('Seems to be a connectivity issue with the camera'),
          );
        });
        break;
    }
  }


  private _streamAttemptEndAction(status: 'success' | 'fail' | 'canceled'): void {
    this._datadogService.addAction('stream_attempt_end', {
      camera_id: this._camId,
      source_ui: PLAYERS_REFERENCE.SOURCE_UI.SLDP,
      protocol: PLAYERS_REFERENCE.PROTOCOL.SLDP,
      status,
    });
  }

  private _displayError(title: string): string {
    return `
    <span class="material-icons"> error </span>
    <p>${title}</p>`;
  }

  private _onConnectionStarted(url: string): void {
    this._datadogService.addAction('sldp_connection_started', {
      camera_id: this._camId,
      stream_url: url,
      buffering: this._sldpSettings.buffering,
      latency_tolerance: this._sldpSettings.latencyTolerance,
      latency_adjust_method: this._sldpSettings.latencyAdjustMethod,
    });
  }

  private _onConnectionEstablished(streams: ReadonlyArray<SldpStreamInfo>): void {
    const streamDetails =
      streams?.map((stream) => ({
        ...(stream.name && { name: stream.name }),
        ...(stream.width && { width: stream.width }),
        ...(stream.height && { height: stream.height }),
        ...(stream.vcodec && { vcodec: stream.vcodec }),
        ...(stream.acodec && { acodec: stream.acodec }),
        ...(stream.video && { video: stream.video }),
        ...(stream.audio && { audio: stream.audio }),
        ...(stream.bandwidth &&
          stream.bandwidth !== '0' &&
          stream.bandwidth !== 0 && { bandwidth: Number(stream.bandwidth) }),
      })) ?? [];

    this._datadogService.addAction('sldp_connection_established', {
      camera_id: this._camId,
      stream_url: this._streamUrl,
      stream_count: streams?.length ?? 0,
      streams: streamDetails,
      buffering: this._sldpSettings.buffering,
      latency_tolerance: this._sldpSettings.latencyTolerance,
      latency_adjust_method: this._sldpSettings.latencyAdjustMethod,
    });
    this._callback();
  }

  private _onConnectionClosed(): void {
    this._datadogService.addAction('sldp_connection_closed', {
      camera_id: this._camId,
      stream_url: this._streamUrl,
      buffering: this._sldpSettings.buffering,
      latency_tolerance: this._sldpSettings.latencyTolerance,
      latency_adjust_method: this._sldpSettings.latencyAdjustMethod,
    });
  }

  private _onLowBuffer(): void {
    this._stallStartTime = performance.now();
    this._lowBufferCount++;
    const timestamp = new Date().getTime();
    this._datadogService.addAction('sldp_low_buffer', {
      camera_id: this._camId,
      stream_url: this._streamUrl,
      buffering: this._sldpSettings.buffering,
      latency_tolerance: this._sldpSettings.latencyTolerance,
      latency_adjust_method: this._sldpSettings.latencyAdjustMethod,
    });
    this._datadogService.addAction('stream_stall_count', {
      timestamp,
      camera_id: this._camId,
      source_ui: PLAYERS_REFERENCE.SOURCE_UI.SLDP,
      protocol: PLAYERS_REFERENCE.PROTOCOL.SLDP,
      stall_count: this._lowBufferCount,
    });
  }

  private _onLatencyAdjustSeek(from: number, to: number): void {
    this._datadogService.addAction('sldp_latency_adjust_seek', {
      camera_id: this._camId,
      stream_url: this._streamUrl,
      seek_from: from,
      seek_to: to,
      buffering: this._sldpSettings.buffering,
      latency_tolerance: this._sldpSettings.latencyTolerance,
      latency_adjust_method: this._sldpSettings.latencyAdjustMethod,
    });
  }
}
